import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import { Plus, Users, Lock, Trophy, ChevronRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Lobbies() {
  const [cities, setCities] = useState([]);
  const [lobbies, setLobbies] = useState([]);
  const [journey, setJourney] = useState(null);
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    const [c, l, j] = await Promise.all([
      axios.get(`${BACKEND_URL}/api/cities`),
      axios.get(`${BACKEND_URL}/api/lobbies`),
      axios.get(`${BACKEND_URL}/api/journey`, { withCredentials: true }).catch(() => ({ data: null })),
    ]);
    setCities(c.data.cities);
    setLobbies(l.data.lobbies);
    setJourney(j.data);
    if (!selected && c.data.cities.length) {
      // Default-select the first UNLOCKED city.
      const unlocked = (j.data?.unlocked_ids) || c.data.cities.map((x) => x.id);
      const first = c.data.cities.find((x) => unlocked.includes(x.id));
      setSelected((first || c.data.cities[0]).id);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line

  const createLobby = async () => {
    if (!selected) return;
    const unlocked = journey?.unlocked_ids;
    if (unlocked && !unlocked.includes(selected)) {
      toast.error("This city is locked — clear more cities to unlock it.");
      return;
    }
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/lobbies`,
        { city_id: selected },
        { withCredentials: true }
      );
      navigate(`/lobbies/${res.data.lobby_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create lobby");
    }
  };

  const joinLobby = async (id) => {
    try {
      await axios.post(`${BACKEND_URL}/api/lobbies/${id}/join`, {}, { withCredentials: true });
      navigate(`/lobbies/${id}`);
    } catch (e) {
      toast.error("Could not join lobby");
    }
  };

  return (
    <AppLayout>
      <div className="px-6 lg:px-12 py-10">
        <div className="label-kicker">Live Ops</div>
        <h1 className="font-heading text-5xl lg:text-6xl mt-1">Lobbies</h1>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* City picker */}
          <div className="lg:col-span-3 surface-solid p-6">
            <div className="flex items-baseline justify-between mb-5">
              <div>
                <div className="label-kicker">Start new race</div>
                <div className="font-heading text-2xl mt-1">Choose the map</div>
              </div>
              <button
                className="btn-primary flex items-center gap-2"
                onClick={createLobby}
                data-testid="lobbies-create-btn"
              >
                <Plus className="w-4 h-4" /> Create Lobby
              </button>
            </div>
            {journey && (
              <div className="mb-4 flex items-center gap-3 text-[11px] tracking-[0.2em] uppercase text-white/50" data-testid="lobbies-journey">
                <Trophy className="w-3.5 h-3.5 text-[#FFCC00]" />
                <span>Cities cleared: <b className="text-white">{journey.cleared_count}/{journey.total_cities}</b></span>
                {journey.next_unlocks.length > 0 && (
                  <span className="ml-auto">Next clear unlocks: <b className="text-[#34C759]">{journey.next_unlocks.join(", ")}</b></span>
                )}
                {journey.world_champion_earned && (
                  <span className="ml-auto text-[#FFCC00]">🏆 WORLD CHAMPION</span>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {cities.map((c) => {
                const active = selected === c.id;
                const unlocked = !journey || journey.unlocked_ids.includes(c.id);
                const cleared = journey && journey.cleared_ids.includes(c.id);
                return (
                  <button
                    key={c.id}
                    data-testid={`lobbies-city-${c.id}`}
                    onClick={() => unlocked && setSelected(c.id)}
                    disabled={!unlocked}
                    className={`relative text-left p-4 border transition-all duration-150 ${
                      !unlocked
                        ? "border-white/5 bg-white/[0.02] text-white/30 cursor-not-allowed"
                        : active
                        ? "border-[#FF3B30] bg-[#FF3B30]/10"
                        : "border-white/10 hover:border-white/30"
                    }`}
                  >
                    <div className="w-2 h-2 rounded-full mb-2" style={{ background: c.accent }} />
                    <div className="font-heading text-lg flex items-center gap-2">
                      {c.name}
                      {cleared && <Trophy className="w-3.5 h-3.5 text-[#FFCC00]" data-testid={`lobbies-city-${c.id}-cleared`} />}
                    </div>
                    <div className="text-[10px] tracking-widest text-white/40 uppercase mt-1">
                      {c.tagline}
                    </div>
                    {!unlocked && (
                      <div className="absolute top-2 right-2" data-testid={`lobbies-city-${c.id}-locked`}>
                        <Lock className="w-3.5 h-3.5 text-white/40" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Open lobbies list */}
          <div className="lg:col-span-2 surface-solid p-6">
            <div className="flex items-baseline justify-between mb-5">
              <div>
                <div className="label-kicker">Open now</div>
                <div className="font-heading text-2xl mt-1">Join a race</div>
              </div>
              <span className="font-mono text-xs text-white/40" data-testid="lobbies-count">
                {lobbies.length}
              </span>
            </div>
            {lobbies.length === 0 ? (
              <div className="text-sm text-white/50 py-10 text-center">
                No open lobbies. Create one to kick off the pursuit.
              </div>
            ) : (
              <div className="space-y-2">
                {lobbies.map((l) => (
                  <button
                    key={l.lobby_id}
                    data-testid={`lobbies-open-${l.lobby_id}`}
                    onClick={() => joinLobby(l.lobby_id)}
                    className="w-full text-left p-4 border border-white/10 hover:border-[#FF3B30] transition-colors duration-150 flex items-center justify-between"
                  >
                    <div>
                      <div className="font-heading text-lg">{l.city_name}</div>
                      <div className="text-xs text-white/50 flex items-center gap-2">
                        <Users className="w-3 h-3" />
                        {l.players.length} · host: {l.host_name}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-white/40" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
